import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconStack2 } from '@tabler/icons-react';
import { BoardSearchListFilterPanel } from '../board-search-filters/BoardSearchListFilterPanel';
import { BoardSearchSimplePickerPanel } from '../board-search-filters/BoardSearchSimplePickerPanel';
import { computeToolbarFilterPopoverLayout } from '../board-search-filters/toolbarFilterPopoverLayout';
import '../board-search-filters/BoardSearchFilters.css';
import './BoardClickUpFilterChip.css';

export function BoardClickUpFilterChipDropdown({
  label,
  options = [],
  selected = [],
  onToggleItem,
  onToggleAll,
  onClear,
  onApply,
  disabled = false,
  isOpen = false,
  onOpenChange,
  searchPlaceholder = 'Pesquisar...',
  showSearch = true,
  countLabel = 'itens',
  listAriaLabel = 'Opções',
  popoverClassName = '',
  headerBanner = null,
  isOptionDisabled,
  emptyMessage = 'Nenhum resultado',
  pickerMode = 'default',
  pickerWidth = 400,
  pickerMaxHeight = 460,
  showSimpleBulkMenu = false,
  icon: Icon = IconStack2,
  getOptionSuffix,
  /** Optional: group label per option (renders section heads like cidade/UF). */
  getOptionGroup,
  /** Optional: custom group head label (icons, etc.). */
  renderOptionGroupLabel,
  /** Show numeric badge on group heads. Default true. */
  showOptionGroupCount = true,
  /** Quando informado, substitui o contador numérico na chip (ex.: label do período). */
  selectionDisplay,
  /** z-index do popover (ex.: acima de modais). */
  popoverZIndex,
}) {
  const anchorRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [layout, setLayout] = useState(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const t = searchTerm.toLowerCase().trim();
    return options.filter((o) => String(o).toLowerCase().includes(t));
  }, [options, searchTerm]);

  const items = useMemo(
    () => filteredOptions.map((value) => ({ value, label: value })),
    [filteredOptions]
  );

  const selectableItems = useMemo(
    () => items.filter((item) => !isOptionDisabled?.(item)),
    [items, isOptionDisabled]
  );

  const updateLayout = () => {
    const el = anchorRef.current;
    if (!el || !isOpen) {
      setLayout(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setLayout(computeToolbarFilterPopoverLayout(rect, pickerWidth, pickerMaxHeight));
  };

  useLayoutEffect(() => {
    updateLayout();
  }, [isOpen, options.length]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      return undefined;
    }
    const onResize = () => updateLayout();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      const t = event.target;
      if (anchorRef.current?.contains(t)) return;
      if (t.closest?.('.board-search-filters-popover')) return;
      onOpenChange?.(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen, onOpenChange]);

  const handleToggleAll = () => {
    const keys = selectableItems.map((i) => i.value);
    const allMarked = keys.length > 0 && keys.every((k) => selected.includes(k));
    if (onToggleAll) {
      onToggleAll(keys, allMarked);
      return;
    }
    if (allMarked) {
      keys.forEach((k) => {
        if (selected.includes(k)) onToggleItem(k);
      });
    } else {
      keys.forEach((k) => {
        if (!selected.includes(k)) onToggleItem(k);
      });
    }
  };

  const hasSelection = selected.length > 0;
  const selectionText =
    selectionDisplay != null && String(selectionDisplay).trim() !== ''
      ? String(selectionDisplay)
      : hasSelection
        ? String(selected.length)
        : '';

  return (
    <>
      <div className="board-clickup-filter-chip-anchor" ref={anchorRef}>
        <button
          type="button"
          className={[
            'board-clickup-filter-chip',
            hasSelection || selectionText ? 'has-selection' : '',
            isOpen ? 'is-open' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          disabled={disabled}
          aria-label={`Filtrar por ${label}`}
          aria-expanded={isOpen}
          onClick={() => !disabled && onOpenChange?.(!isOpen)}
        >
          <span className="board-clickup-filter-chip-icon" aria-hidden="true">
            <Icon size={13} stroke={1.5} />
          </span>
          <span className="board-clickup-filter-chip-text">
            <span className="board-clickup-filter-chip-label">{label}</span>
            {selectionText ? (
              <>
                <span className="board-clickup-filter-chip-separator">:</span>
                <span className="board-clickup-filter-chip-value">{selectionText}</span>
              </>
            ) : null}
          </span>
        </button>
      </div>

      {isOpen && layout
        ? ReactDOM.createPortal(
            <div
              className={[
                'board-search-filters-popover',
                pickerMode === 'simple' ? 'board-search-filters-popover--simple' : '',
                popoverClassName,
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                position: layout.position,
                top: layout.top,
                left: layout.left,
                width: layout.width,
                boxSizing: layout.boxSizing,
                zIndex: popoverZIndex != null ? popoverZIndex : layout.zIndex,
                maxHeight: layout.maxHeight,
                '--bsf-cidade-popover-max-h': layout['--bsf-cidade-popover-max-h'],
                transform: layout.flipped ? 'translateY(-100%)' : undefined,
              }}
              role="dialog"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {pickerMode === 'simple' ? (
                <BoardSearchSimplePickerPanel
                  headerBanner={headerBanner}
                  searchPlaceholder={searchPlaceholder}
                  showSearch={showSearch}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  items={items}
                  getKey={(i) => i.value}
                  getLabel={(i) => i.label}
                  getSuffix={
                    typeof getOptionSuffix === 'function'
                      ? (i) => getOptionSuffix(i.value)
                      : undefined
                  }
                  getItemGroup={
                    typeof getOptionGroup === 'function'
                      ? (i) => getOptionGroup(i.value)
                      : undefined
                  }
                  renderGroupLabel={
                    typeof renderOptionGroupLabel === 'function' ? renderOptionGroupLabel : undefined
                  }
                  showGroupCount={showOptionGroupCount}
                  isItemDisabled={isOptionDisabled}
                  selectedKeys={selected}
                  onToggle={(item) => onToggleItem(item.value)}
                  emptyMessage={emptyMessage}
                  listAriaLabel={listAriaLabel}
                  bulkMenuEnabled={showSimpleBulkMenu}
                  onBulkToggleAll={
                    showSimpleBulkMenu && selectableItems.length > 0
                      ? handleToggleAll
                      : undefined
                  }
                />
              ) : (
                <BoardSearchListFilterPanel
                  headerBanner={headerBanner}
                  searchPlaceholder={searchPlaceholder}
                  showSearch={showSearch}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  countLabel={countLabel}
                  items={items}
                  getKey={(i) => i.value}
                  getLabel={(i) => i.label}
                  isItemDisabled={isOptionDisabled}
                  selectedKeys={selected}
                  onToggle={(item) => onToggleItem(item.value)}
                  onToggleAll={selectableItems.length > 1 ? handleToggleAll : undefined}
                  emptyMessage={emptyMessage}
                  listAriaLabel={listAriaLabel}
                  onClear={() => {
                    onClear?.();
                  }}
                  onApply={() => {
                    onApply?.();
                    onOpenChange?.(false);
                  }}
                  applyLabel={`Aplicar (${selected.length})`}
                />
              )}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
